import { HealthInsuranceData, PopulationData, PvCostData, PvSizeData, supabaseClient, VMTData } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

/** Throws if Supabase returned an error object. */
function throwOnSupabaseError(error: { message: string } | null | undefined): void {
  if (error) throw error;
}

/** Lookup location_name, geography_type, county from the population table for a given geofips. */
async function fetchLocationInfo(geofips: number): Promise<{
  location_name: string;
  geography_type: string;
  county: string | null;
} | null> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('location_name, geography_type, county')
    .eq('geofips', geofips)
    .limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0] as { location_name: string; geography_type: string; county: string | null };
}

/** Resolve city geofips to its parent county geofips; returns original geofips for counties. */
async function resolveCountyGeofips(
  geofips: number,
  locationInfo: { geography_type: string; county?: string | null; location_name?: string } | null
): Promise<number> {
  if (!locationInfo || locationInfo.geography_type !== 'City') return geofips;
  const countyName = locationInfo.county || `${locationInfo.location_name} County`;
  const { data, error } = await supabaseClient
    .from('population')
    .select('geofips')
    .eq('location_name', countyName)
    .eq('geography_type', 'County')
    .limit(1);
  if (error || !data || data.length === 0) return geofips;
  return data[0].geofips;
}

/** Deduplicate records by year, keeping the first occurrence. Returns year -> numeric value map. */
function deduplicateByYear(
  records: Record<string, unknown>[],
  valueField: string
): Map<number, number> {
  const map = new Map<number, number>();
  for (const record of records) {
    const y = Number(record.year);
    if (!Number.isFinite(y) || map.has(y)) continue;
    map.set(y, Number(record[valueField]) || 0);
  }
  return map;
}

/** Compute year-over-year ratios (current / next) from a year->value map. Returns descending by year. */
function computeYearOverYearRatios(
  valuesByYear: Map<number, number>,
  opts?: { minYear?: number; maxYear?: number }
): { year: number; ratio: number }[] {
  const sortedYears = Array.from(valuesByYear.keys()).sort((a, b) => a - b);
  const ratios: { year: number; ratio: number }[] = [];
  for (let i = 0; i < sortedYears.length - 1; i++) {
    const currentYear = sortedYears[i];
    const nextYear = sortedYears[i + 1];
    if (opts?.minYear != null && currentYear < opts.minYear) continue;
    if (opts?.maxYear != null && currentYear > opts.maxYear) continue;
    const currentValue = valuesByYear.get(currentYear)!;
    const nextValue = valuesByYear.get(nextYear)!;
    if (currentValue > 0 && nextValue > 0) {
      ratios.push({ year: currentYear, ratio: currentValue / nextValue });
    }
  }
  return ratios.sort((a, b) => b.year - a.year);
}

/** Aggregate total_vmt by year (summing across fuel types, etc.). */
function aggregateVmtByYear(records: Record<string, unknown>[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const record of records) {
    const year = Number(record.year);
    if (!Number.isFinite(year)) continue;
    map.set(year, (map.get(year) || 0) + (Number(record.total_vmt) || 0));
  }
  return map;
}

/** Fetch EMFAC Fleet data, aggregate by year, compute year-over-year ratios. */
async function fetchEmfacFleetRatios(
  geofips: number,
  tableName: string
): Promise<{ year: number; vmt_ratio: number | null; total_vmt: number }[]> {
  const locationInfo = await fetchLocationInfo(geofips);
  if (!locationInfo) {
    console.error('Error fetching location data');
    throw new Error('Error fetching location data');
  }
  const geographyType = locationInfo.geography_type === 'County' ? 'County' : 'City';

  const { data, error } = await supabaseClient
    .from(tableName)
    .select('year, vmt_ratio, total_vmt')
    .eq('geofips', geofips)
    .eq('geography_type', geographyType)
    .eq('class', 'LDV')
    .in('fuel_type', ['Gasoline', 'Diesel'])
    .gte('year', 2015)
    .lte('year', 2022)
    .order('year', { ascending: false });

  if (error || !data || data.length === 0) {
    if (error) {
      console.error(`❌ ${tableName} ${geographyType} query error:`, error);
    }
    return [];
  }

  const yearMap = aggregateVmtByYear(data as Record<string, unknown>[]);
  const sortedYears = Array.from(yearMap.keys()).sort((a, b) => b - a);
  const result = sortedYears.map(year => {
    const currentVMT = yearMap.get(year) || 0;
    const nextYearVMT = yearMap.get(year + 1) ?? null;
    const vmt_ratio = (nextYearVMT !== null && nextYearVMT > 0 && currentVMT > 0)
      ? currentVMT / nextYearVMT
      : null;
    return { year, vmt_ratio, total_vmt: currentVMT };
  });
  return result;
}

/** PostgREST sometimes returns `year` / `vmt` as strings; chart and ratio lookups use strict numeric equality. */
function normalizeReplicaVmtRows(rows: VMTData[] | null | undefined): VMTData[] {
  return (rows ?? []).map((r) => {
    const rawY = (r as { year?: unknown }).year;
    const rawV = (r as { vmt?: unknown }).vmt;
    const y = rawY == null ? NaN : Number(rawY);
    const v = rawV == null ? 0 : Number(rawV);
    return {
      ...r,
      year: Number.isFinite(y) ? y : 0,
      vmt: Number.isFinite(v) ? v : 0,
    };
  });
}

/**
 * replica_vmt_by_mode can have duplicate (geofips, year, mode) with different location_name ("X" vs "X, CA").
 * That double-counts if summed; `.find()` can pick an arbitrary row. Keep one row per (year, mode) like
 * sql/deduplicate_replica_vmt_by_mode.sql.
 */
function dedupeReplicaVmtRows(rows: VMTData[]): VMTData[] {
  const m = new Map<string, VMTData>();
  for (const r of rows) {
    const y = Number(r.year);
    const mode = String(r.mode ?? '');
    if (!Number.isFinite(y)) continue;
    const key = `${y}|${mode}`;
    const existing = m.get(key);
    if (!existing) {
      m.set(key, r);
      continue;
    }
    const name = String((r as { location_name?: string }).location_name ?? '');
    const exName = String((existing as { location_name?: string }).location_name ?? '');
    const rGeo = String((r as { geography_type?: string }).geography_type ?? '').toLowerCase();
    const eGeo = String((existing as { geography_type?: string }).geography_type ?? '').toLowerCase();
    // Prefer Census "place" over "City" when both exist (aligns with sql/deduplicate_replica_vmt_by_mode.sql).
    if (eGeo === 'city' && rGeo === 'place') {
      m.set(key, r);
      continue;
    }
    if (rGeo === 'city' && eGeo === 'place') {
      continue;
    }
    const preferR =
      exName.includes(', CA') && !name.includes(', CA')
        ? true
        : !exName.includes(', CA') && name.includes(', CA')
          ? false
          : (Number(r.vmt) || 0) >= (Number(existing.vmt) || 0);
    m.set(key, preferR ? r : existing);
  }
  return Array.from(m.values());
}

function normalizeAndDedupeReplicaVmt(rows: VMTData[] | null | undefined): VMTData[] {
  return dedupeReplicaVmtRows(normalizeReplicaVmtRows(rows));
}

const REPLICA_LDV_MODES = ['PRIVATE_AUTO', 'ON_DEMAND_AUTO'] as const;

async function fetchLatestPopulationSnapshotForGeofips(geofips: number): Promise<{
  population: number;
  geography_type: string | null;
}> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('population, geography_type')
    .eq('geofips', geofips)
    .order('year', { ascending: false })
    .limit(1);

  if (error || !data?.[0]) {
    return { population: 0, geography_type: null };
  }
  return {
    population: Number(data[0].population) || 0,
    geography_type: data[0].geography_type != null ? String(data[0].geography_type) : null,
  };
}

/**
 * For Replica VMT, prefer **2023** population (matches Replica anchor year). Using only
 * `ORDER BY year DESC LIMIT 1` can pick a bad/projected row.
 */
async function fetchPopulationForReplicaPlausibility(geofips: number): Promise<{
  population: number;
  geography_type: string | null;
}> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('population, geography_type, year')
    .eq('geofips', geofips)
    .in('year', [2021, 2022, 2023, 2024]);

  if (error || !data?.length) {
    return fetchLatestPopulationSnapshotForGeofips(geofips);
  }
  const y2023 = data.find((r) => Number(r.year) === 2023);
  if (y2023) {
    const p = Number(y2023.population) || 0;
    if (p > 0) {
      return {
        population: p,
        geography_type: y2023.geography_type != null ? String(y2023.geography_type) : null,
      };
    }
  }
  const withPop = data
    .map((r) => ({
      population: Number(r.population) || 0,
      geography_type: r.geography_type != null ? String(r.geography_type) : null,
      year: Number(r.year),
    }))
    .filter((r) => r.population > 0);
  if (withPop.length === 0) {
    return fetchLatestPopulationSnapshotForGeofips(geofips);
  }
  withPop.sort((a, b) => b.year - a.year);
  const chosen = withPop[0];
  return { population: chosen.population, geography_type: chosen.geography_type };
}

/**
 * When Replica has multiple geography_type values for the same geofips/year (e.g. City vs place),
 * pick the city-scale bucket. Prefer the population table's geography_type when it has both LDV modes;
 * otherwise prefer the lowest per-capita LDV bucket (avoids county-scale dupes on a city geofips).
 */
function pickReplicaGeographyTypeKey(
  rows: { geography_type?: unknown; year?: unknown; mode?: unknown; vmt?: unknown }[],
  anchorYear: number,
  population: number,
  popGeographyType: string | null
): string | null {
  const ldv = rows.filter(
    (r) =>
      Number(r.year) === anchorYear &&
      REPLICA_LDV_MODES.includes(String(r.mode) as (typeof REPLICA_LDV_MODES)[number])
  );
  if (ldv.length === 0) return null;

  const byGeo = new Map<string, typeof ldv>();
  for (const r of ldv) {
    const g = String(r.geography_type ?? '_unknown').toLowerCase();
    const arr = byGeo.get(g) ?? [];
    arr.push(r);
    byGeo.set(g, arr);
  }

  if (byGeo.size === 1) return null;

  const pop = Math.max(population, 1);
  const sumGroup = (group: typeof ldv) => group.reduce((s, r) => s + (Number(r.vmt) || 0), 0);

  const scored = Array.from(byGeo.entries()).map(([geo, group]) => {
    const sum = sumGroup(group);
    const perCap = sum / pop;
    const modes = new Set(group.map((row) => String(row.mode)));
    const hasBoth = modes.has('PRIVATE_AUTO') && modes.has('ON_DEMAND_AUTO');
    return { geo, sum, perCap, hasBoth };
  });

  if (popGeographyType) {
    const want = popGeographyType.toLowerCase();
    const match = scored.find((s) => s.geo === want);
    if (match?.hasBoth) return want;
  }

  const withBoth = scored.filter((s) => s.hasBoth);
  const candidates = withBoth.length > 0 ? withBoth : scored;
  return candidates.sort((a, b) => a.perCap - b.perCap)[0].geo;
}

function filterReplicaRowsByGeographyKey(rows: VMTData[], geoKey: string | null): VMTData[] {
  if (!geoKey) return rows;
  const narrowed = rows.filter(
    (r) =>
      String((r as VMTData & { geography_type?: unknown }).geography_type ?? '').toLowerCase() === geoKey
  );
  return narrowed.length > 0 ? narrowed : rows;
}

export class DataService {
  private static async fetchTableByYear<T>(
    table: string,
    filters?: { geofips?: number; location?: string }
  ): Promise<T[]> {
    let query = supabaseClient.from(table).select('*').order('year', { ascending: true });
    if (filters?.geofips) query = query.eq('geofips', filters.geofips);
    if (filters?.location) query = query.ilike('location_name', `%${filters.location}%`);
    const { data, error } = await query;
    throwOnSupabaseError(error);
    return (data || []) as T[];
  }

  static async getPopulationData(): Promise<PopulationData[]> {
    return this.fetchTableByYear<PopulationData>('population');
  }

  static async getPopulationDataByGeofips(geofips: number): Promise<PopulationData[]> {
    return this.fetchTableByYear<PopulationData>('population', { geofips });
  }

  static async getHealthInsuranceData(location?: string): Promise<HealthInsuranceData[]> {
    return this.fetchTableByYear<HealthInsuranceData>('percent_no_health_insure', { location });
  }

  static async getPvCostData(location?: string): Promise<PvCostData[]> {
    return this.fetchTableByYear<PvCostData>('all_pv_cost', { location });
  }

  static async getPvSizeData(location?: string): Promise<PvSizeData[]> {
    return this.fetchTableByYear<PvSizeData>('all_pv_size', { location });
  }

  /** Burdens and Benefits (Overview): equity indicators summary for the location, used by the Climate Equity Index. */
  static async getBurdensAndBenefitsData(geofips: number): Promise<Record<string, unknown>[]> {
    const geofipsStr = String(geofips);
    const candidates = [geofipsStr, geofipsStr.padStart(5, '0'), geofipsStr.padStart(7, '0')];
    const { data, error } = await supabaseClient
      .from('equity_indicators_summary')
      .select('*')
      .in('geofips', candidates)
      .order('year', { ascending: true });
    throwOnSupabaseError(error);
    return (data || []) as Record<string, unknown>[];
  }

  // Get all available locations
  static async getAvailableLocations(): Promise<string[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('location_name')
      .order('location_name');
    
    throwOnSupabaseError(error);
    // Fix TypeScript Set iteration issue
    return Array.from(new Set(data?.map(row => row.location_name) || []));
  }

  // Get city-county mappings from Supabase location service
  static async getCityCountyMappings(): Promise<Record<string, string>> {
    try {
      // Ensure the location service is initialized
      await supabaseLocationService.initialize();
      
      // Get all available locations
      const locations = supabaseLocationService.getAvailableLocations();
      
      // Build city-county mappings
      const mappings: Record<string, string> = {};
      
      locations.forEach(location => {
        if (location.type === 'city' && location.county) {
          mappings[location.name.toLowerCase()] = location.county;
        }
      });
      
      return mappings;
      
    } catch (error) {
      console.error('Error loading city-county mappings from Supabase:', error);
      // Return empty mappings as fallback
      return {};
    }
  }

  // VMT data by mode (raw Replica data)
  static async getVMTData(location?: string, modes?: string[], geofips?: number): Promise<VMTData[]> {
    // Build a strictly filtered query (no broad sampling)
    let query = supabaseClient
      .from('replica_vmt_by_mode')
      .select('*')
      .order('year', { ascending: true });
    
    if (geofips) {
      query = query.eq('geofips', geofips);
    } else if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    if (modes && modes.length > 0) {
      query = query.in('mode', modes);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('VMT query error:', error);
      throw error;
    }

    let rows = data || [];
    if (geofips && rows.length > 0) {
      const popSnap = await fetchPopulationForReplicaPlausibility(geofips);
      const years = Array.from(new Set(rows.map((r) => Number((r as { year?: unknown }).year)))).filter((y) =>
        Number.isFinite(y)
      );
      const anchorYear = years.includes(2023) ? 2023 : years.length > 0 ? Math.max.apply(null, years) : 2023;
      const geoKey = pickReplicaGeographyTypeKey(rows as VMTData[], anchorYear, popSnap.population, popSnap.geography_type);
      rows = filterReplicaRowsByGeographyKey(rows as VMTData[], geoKey);
    }

    return normalizeAndDedupeReplicaVmt(rows);
  }

  // VMT data by geofips (raw Replica data)
  static async getVMTDataByGeofips(geofips?: number): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT data lookup');
      throw new Error('Geofips is required for VMT data lookup');
    }
    
    try {
      const { data, error } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (error) {
        console.error('Error fetching VMT data by geofips:', error);
        throw error;
      }

      let rows = data || [];
      if (rows.length > 0) {
        const popSnap = await fetchPopulationForReplicaPlausibility(geofips);
        const years = Array.from(new Set(rows.map((r) => Number((r as { year?: unknown }).year)))).filter((y) =>
          Number.isFinite(y)
        );
        const anchorYear = years.includes(2023) ? 2023 : years.length > 0 ? Math.max.apply(null, years) : 2023;
        const geoKey = pickReplicaGeographyTypeKey(
          rows as VMTData[],
          anchorYear,
          popSnap.population,
          popSnap.geography_type
        );
        rows = filterReplicaRowsByGeographyKey(rows as VMTData[], geoKey);
      }

      return normalizeAndDedupeReplicaVmt(rows);
    } catch (error) {
      console.error('Error in getVMTDataByGeofips:', error);
      throw error;
    }
  }

  // VMT data with scaling factors (2015-2022 backcasting)
  static async getVMTDataWithScaling(geofips?: number, backcastingMethod: 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined' = 'AADT', emfacVersion: '2021' | '2025' = '2025', emfacFleetVersion: '2021' | '2025' = '2025'): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT scaling calculation');
      throw new Error('Geofips is required for VMT scaling calculation');
    }

    try {
      // Get the 2023 VMT data from Replica (used for all methods, no scaling)
      let { data: vmt2023Data, error: vmtError } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .eq('year', 2023);

      if (vmtError) {
        console.error('Error fetching 2023 VMT data:', vmtError);
        throw vmtError;
      }

      const popSnap = await fetchPopulationForReplicaPlausibility(geofips);
      if (vmt2023Data && vmt2023Data.length > 0) {
        const geoKey = pickReplicaGeographyTypeKey(vmt2023Data, 2023, popSnap.population, popSnap.geography_type);
        vmt2023Data = filterReplicaRowsByGeographyKey(vmt2023Data as VMTData[], geoKey);
      }

      // If no 2023 data, try to get the latest available year as fallback
      if (!vmt2023Data || vmt2023Data.length === 0) {
        const { data: latestVmtData, error: latestError } = await supabaseClient
          .from('replica_vmt_by_mode')
          .select('*')
          .eq('geofips', geofips)
          .order('year', { ascending: false })
          .limit(100);
        
        if (!latestError && latestVmtData && latestVmtData.length > 0) {
          const latestYear = Math.max(...latestVmtData.map((d: any) => Number(d.year)));
          let fallbackRows = latestVmtData.filter((d: any) => Number(d.year) === latestYear);
          const geoKeyFb = pickReplicaGeographyTypeKey(
            fallbackRows,
            latestYear,
            popSnap.population,
            popSnap.geography_type
          );
          fallbackRows = filterReplicaRowsByGeographyKey(fallbackRows as VMTData[], geoKeyFb);
          vmt2023Data = fallbackRows;
        } else {
          console.error(`No VMT data found for geofips ${geofips} in any year`);
          return [];
        }
      }

      vmt2023Data = normalizeAndDedupeReplicaVmt(vmt2023Data || []);

      // Determine the baseline year (might be 2023 or earlier if 2023 data not available)
      const baselineYear = vmt2023Data.length > 0 ? vmt2023Data[0].year : 2023;

      // Fetch AADT data if needed (for AADT or Combined method)
      // Calculate AADT ratios: current_year / next_year (same as EMFAC and Fleet)
      let aadtRatios: any[] = [];
      if (backcastingMethod === 'AADT' || backcastingMethod === 'Combined') {
        try {
          const { data: aadtData, error: aadtError } = await supabaseClient
            .from('aadt_filtered')
            .select('year, value')
            .eq('geofips', geofips)
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!aadtError && aadtData && aadtData.length > 0) {
            const uniqueAadt = deduplicateByYear(aadtData as Record<string, unknown>[], 'value');
            aadtRatios = computeYearOverYearRatios(uniqueAadt);
          }
        } catch {
          // AADT table not available
        }
      }

      // Fetch Population data if needed (for Population or Combined method)
      let populationRatios: any[] = [];
      if (backcastingMethod === 'Population' || backcastingMethod === 'Combined') {
        try {
          const { data: populationData, error: populationError } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!populationError && populationData && populationData.length > 0) {
            const uniquePopulation = deduplicateByYear(populationData as Record<string, unknown>[], 'population');
            populationRatios = computeYearOverYearRatios(uniquePopulation, { minYear: 2015, maxYear: 2022 });
          }
        } catch {
          // Population table not available
        }
      }

      // Fetch EMFAC County data if needed (for EMFAC2025 or Combined method)
      let emfacCountyData: any[] = [];
      if (backcastingMethod === 'EMFAC2025' || backcastingMethod === 'Combined') {
        try {
          const locationInfo = await fetchLocationInfo(geofips);
          if (!locationInfo) {
            console.error('Error fetching location data');
          } else {
            const countyGeofips = await resolveCountyGeofips(geofips, locationInfo);

            const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
            const { data: emfacData, error: emfacError } = await supabaseClient
              .from(tableName)
              .select('year, backcasting_ratio, total_vmt')
              .eq('geofips', countyGeofips)
              .eq('class', 'LDV')
              .gte('year', 2015)
              .lte('year', 2022)
              .order('year', { ascending: false });

            if (!emfacError && emfacData) {
              emfacCountyData = emfacData.map(record => ({
                year: Number(record.year),
                vmt_ratio: record.backcasting_ratio,
                total_vmt: record.total_vmt
              }));
            }
          }
        } catch {
          // EMFAC County data not available
        }
      }

      // Fetch EMFAC Fleet data for 2015-2022 backcasting
      let emfacFleetData: any[] = [];
      if (backcastingMethod === 'EMFAC Fleet' || backcastingMethod === 'Combined') {
        try {
          emfacFleetData = await fetchEmfacFleetRatios(geofips, 'emfac2025_fleet_mat');
        } catch {
          // EMFAC Fleet data not available
        }
      }

      // Fetch EMFAC2021 Fleet data for 2015-2022 backcasting
      let emfac2021FleetData: any[] = [];
      if (backcastingMethod === 'EMFAC2021 Fleet' || backcastingMethod === 'Combined') {
        try {
          emfac2021FleetData = await fetchEmfacFleetRatios(geofips, 'emfac2021_fleet_mat');
        } catch {
          // EMFAC2021 Fleet data not available
        }
      }

      // Create backcasted VMT data for each mode using selected backcasting method
      const backcastedData: VMTData[] = [];
      
      for (const mode2023 of vmt2023Data) {
        const vmt2023 = Number(mode2023.vmt) || 0;
        const baseYear = Number(mode2023.year) || baselineYear;
        
        // Start with baseline year data
        backcastedData.push({
          ...mode2023,
          year: baseYear
        });
        
        // If baseline is not 2023, add 2023 data using the same VMT
        if (baseYear < 2023) {
          backcastedData.push({
            ...mode2023,
            year: 2023,
            vmt: vmt2023
          });
        }
        
        // Backcast year by year from 2022 down to 2015
        // All methods use the same sequential ratio application:
        // VMT_year = VMT_year+1 × ratio, where ratio = current_year / next_year
        let previousVMT = vmt2023;
        
        type RatioLookup = (y: number) => number | null;
        const ratioLookups: Record<string, RatioLookup> = {
          'AADT': (y) => aadtRatios.find((d: any) => d.year === y)?.ratio ?? null,
          'EMFAC2025': (y) => emfacCountyData.find((d: any) => d.year === y)?.vmt_ratio ?? null,
          'EMFAC Fleet': (y) => emfacFleetData.find((d: any) => d.year === y)?.vmt_ratio ?? null,
          'EMFAC2021 Fleet': (y) => emfac2021FleetData.find((d: any) => d.year === y)?.vmt_ratio ?? null,
          'Population': (y) => populationRatios.find((d: any) => d.year === y)?.ratio ?? null,
        };

        for (let year = 2022; year >= 2015; year--) {
          let scalingFactor: number | null = null;

          if (backcastingMethod === 'Combined') {
            const ratios = Object.values(ratioLookups)
              .map(lookup => lookup(year))
              .filter((r): r is number => r != null && r > 0);
            if (ratios.length > 0) {
              scalingFactor = ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
            }
          } else {
            const lookup = ratioLookups[backcastingMethod];
            if (lookup) {
              const ratio = lookup(year);
              if (ratio != null && ratio > 0) {
                scalingFactor = ratio;
              }
            }
          }
          
          if (scalingFactor !== null && scalingFactor > 0) {
            // Apply ratio sequentially: VMT_year = VMT_year+1 × scaling_factor
            const backcastedVMT = Math.round(previousVMT * scalingFactor);
            
            backcastedData.push({
              ...mode2023,
              year: year,
              vmt: backcastedVMT
            });
            
            // Update previous VMT for next iteration
            previousVMT = backcastedVMT;
          } else {
            backcastedData.push({
              ...mode2023,
              year: year,
              vmt: previousVMT
            });
          }
        }
      }

      // Debug logging removed - VMT backcasting working correctly

      return backcastedData;
      
    } catch (error) {
      console.error('Error in getVMTDataWithScaling:', error);
      throw error;
    }
  }

  // EMFAC backcasting data for VMT historical reconstruction
  static async getEMFACBackcastingData(location: string, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
    
    try {
      // First, determine the county for the given location
      let countyName = location;
      
      // If it's a city, we need to map it to its county
      if (!location.toLowerCase().includes('county')) {
        
        // Use the existing city-county mapping logic from getCityCountyMappings
        const mappings = await this.getCityCountyMappings();
        const locationKey = location.toLowerCase();
        
        // Try to find a mapping
        const mapping = Object.entries(mappings).find(([city, county]) => 
          city.toLowerCase().includes(locationKey) || 
          locationKey.includes(city.toLowerCase())
        );
        
        if (mapping) {
          countyName = mapping[1];
        } else {
        }
      }
      
      // Select table based on EMFAC version
      const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
      
      // Fetch EMFAC data for the county
      const { data, error } = await supabaseClient
        .from(tableName)
        .select('*')
        .ilike('location_name', `%${countyName}%`)
        .in('year', years)
        .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
        .order('year', { ascending: true });
      
      if (error) {
        console.error('EMFAC query error:', error);
        return [];
      }
      
      return data || [];
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingData:', error);
      return [];
    }
  }

  // EMFAC backcasting data by geofips
  static async getEMFACBackcastingDataByGeofips(geofips?: number, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], method: 'EMFAC2025' | 'Population' | 'Combined' = 'EMFAC2025'): Promise<any[]> {
    
    if (!geofips) {
      console.error('Geofips is required for EMFAC data lookup');
      throw new Error('Geofips is required for EMFAC data lookup');
    }
    
    try {
      const locationInfo = await fetchLocationInfo(geofips);
      if (!locationInfo) {
        return [];
      }

      const countyGeofips = await resolveCountyGeofips(geofips, locationInfo);

      if (method === 'Combined') {
        // Fetch both EMFAC2025 and Population data, then average the ratios
        const allYears = [...years, 2015];
        
        // Fetch EMFAC2025 data
        const tableName = 'emfac2025_county_filtered';
        const { data: emfacData, error: emfacError } = await supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', countyGeofips)
          .in('year', allYears)
          .in('class', ['LDV', 'HDV'])
          .order('year', { ascending: true })
          .limit(50000);

        // Fetch Population data
        const { data: popData, error: popError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', geofips)
          .in('year', allYears)
          .order('year', { ascending: true });

        if (emfacError) {
          console.error('Error fetching EMFAC2025 data for Combined method:', emfacError);
        }
        if (popError) {
          console.error('Error fetching Population data for Combined method:', popError);
        }

        // Process EMFAC2025 ratios
        const emfacRatios = new Map<number, number>();
        if (emfacData && emfacData.length > 0) {
          for (const year of years) {
            const yearLDVData = emfacData.find(
              (record) => Number(record.year) === year && record.class === 'LDV'
            );
            if (yearLDVData) {
              let ratio: number | null = null;
              if (yearLDVData.backcasting_ratio !== null && yearLDVData.backcasting_ratio !== undefined) {
                ratio = Number(yearLDVData.backcasting_ratio);
              } else {
                const emfac2015LDV = emfacData.find(
                  (record) => Number(record.year) === 2015 && record.class === 'LDV'
                );
                const emfac2015LDVVMT = emfac2015LDV ? emfac2015LDV.total_vmt : 0;
                if (emfac2015LDVVMT > 0 && yearLDVData.total_vmt) {
                  ratio = yearLDVData.total_vmt / emfac2015LDVVMT;
                }
              }
              if (ratio !== null) {
                emfacRatios.set(year, ratio);
              }
            }
          }
        }

        // Process Population ratios
        const popRatios = new Map<number, number>();
        if (popData && popData.length > 0) {
          const popByYear = new Map<number, number>();
          for (const record of popData) {
            const y = Number(record.year);
            if (Number.isFinite(y)) {
              popByYear.set(y, Number(record.population) || 0);
            }
          }
          for (const year of years) {
            const currentPop = popByYear.get(year);
            const nextYear = year + 1;
            const nextPop = popByYear.get(nextYear);
            if (currentPop && nextPop && nextPop > 0) {
              const ratio = currentPop / nextPop;
              popRatios.set(year, ratio);
            }
          }
        }

        // Average the ratios for each year
        const ratioData = [];
        for (const year of years) {
          const ratios: number[] = [];
          
          const emfacRatio = emfacRatios.get(year);
          if (emfacRatio !== undefined) {
            ratios.push(emfacRatio);
          }
          
          const popRatio = popRatios.get(year);
          if (popRatio !== undefined) {
            ratios.push(popRatio);
          }

          if (ratios.length > 0) {
            const avgRatio = ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
            ratioData.push({
              year: year,
              ratio: avgRatio,
              total_vmt: 0
            });
            // Removed excessive logging - ratios are calculated correctly
          }
        }

        return ratioData;
      } else if (method === 'Population') {
        // Fetch population data for the location (use the original geofips, not county)
        const allYears = [...years, 2015]; // Include 2015 for baseline calculation
        
        const { data: popData, error: popError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', geofips)
          .in('year', allYears)
          .order('year', { ascending: true });

        if (popError) {
          console.error('Error fetching population data by geofips:', popError);
          throw popError;
        }

        // Calculate population-based ratios: current_year_pop / next_year_pop
        if (popData && popData.length > 0) {
          const ratioData = [];
          const popByYear = new Map<number, number>();
          for (const record of popData) {
            const y = Number(record.year);
            if (Number.isFinite(y)) {
              popByYear.set(y, Number(record.population) || 0);
            }
          }

          for (const year of years) {
            const currentPop = popByYear.get(year);
            const nextYear = year + 1;
            const nextPop = popByYear.get(nextYear);

            if (currentPop && nextPop && nextPop > 0) {
              const ratio = currentPop / nextPop;
              ratioData.push({
                year: year,
                ratio: ratio,
                total_vmt: 0
              });
            }
          }

          return ratioData;
        }

        return [];
      } else {
        // EMFAC2025 method (default)
        // Fetch EMFAC data for the county geofips (include 2015 for baseline)
        const allYears = [...years, 2015]; // Include 2015 for baseline calculation
        const tableName = 'emfac2025_county_filtered';
        const { data, error } = await supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', countyGeofips)
          .in('year', allYears)
          .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
          .order('year', { ascending: true })
          .limit(50000); // High limit to ensure we get all data

        if (error) {
          console.error('Error fetching EMFAC data by geofips:', error);
          throw error;
        }

        // Process the data to create ratio fields for backcasting
        if (data && data.length > 0) {
          // Use backcasting_ratio from the filtered view if available (preferred method)
          // Otherwise, calculate manually from total_vmt
          const ratioData = [];
          for (const year of years) {
            const yearLDVData = data.find(
              (record) => Number(record.year) === year && record.class === 'LDV'
            );
            if (yearLDVData) {
              // Prefer backcasting_ratio from filtered view if available
              let ratio: number | null = null;
              if (yearLDVData.backcasting_ratio !== null && yearLDVData.backcasting_ratio !== undefined) {
                ratio = Number(yearLDVData.backcasting_ratio);
              } else {
                // Fallback: calculate ratio manually if backcasting_ratio not available
                const emfac2015LDV = data.find(
                  (record) => Number(record.year) === 2015 && record.class === 'LDV'
                );
                const emfac2015LDVVMT = emfac2015LDV ? emfac2015LDV.total_vmt : 0;
                if (emfac2015LDVVMT > 0 && yearLDVData.total_vmt) {
                  ratio = yearLDVData.total_vmt / emfac2015LDVVMT;
                }
              }
              
              if (ratio !== null) {
                ratioData.push({
                  year: year,
                  ratio: ratio,
                  total_vmt: yearLDVData.total_vmt || 0
                });
              }
            }
          }

          return ratioData;
        }

        return [];
      }
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingDataByGeofips:', error);
      return [];
    }
  }

  // VMT per capita data using the exact same VMT values as the main VMT chart
  static async getVMTPerCapitaData(geofips?: number): Promise<any[]> {
    if (!geofips) {
      console.error('Geofips is required for VMT per capita calculation');
      throw new Error('Geofips is required for VMT per capita calculation');
    }

    try {
      // Get VMT data using the EXACT same method as the main VMT chart
      const locationVMTData = await this.getVMTDataWithScaling(geofips);
      
      if (!locationVMTData || locationVMTData.length === 0) {
        console.error('No VMT data found for location with geofips:', geofips);
        throw new Error('No VMT data available for this location');
      }

      // Get population data for the selected location
      const locationName = locationVMTData[0]?.location_name;

      // Try to get population by geofips first, then fallback to location_name lookup
      let locationPopulationData: any[] = [];
      
      const { data: popByGeofips, error: popGeofipsError } = await supabaseClient
        .from('population')
        .select('year, population, geofips')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (popGeofipsError || !popByGeofips || popByGeofips.length === 0) {
        const normalizedLocationName = locationName.toLowerCase().trim();
        const { data: popByName, error: popNameError } = await supabaseClient
          .from('population')
          .select('year, population, location_name, geofips')
          .or(`location_name.ilike.%${normalizedLocationName}%`)
          .order('year', { ascending: true });

        if (popNameError || !popByName || popByName.length === 0) {
          console.error('No population data found by location name either');
          throw new Error(`No population data found for ${locationName} (geofips: ${geofips})`);
        } else {
          locationPopulationData = popByName;
        }
      } else {
        locationPopulationData = popByGeofips;
      }

      // Use the exact same VMT calculation as the main VMT chart:
      // Sum PRIVATE_AUTO and ON_DEMAND_AUTO modes for each year
      const vmtByYear = locationVMTData
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt,
              location_name: curr.location_name
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      // Calculate VMT per capita for selected location (VMT from main chart ÷ population)
      const locationVMTPerCapita = vmtByYear.map((vmt: any) => {
        const population = locationPopulationData.find(p => p.year === vmt.year);
        const vmtPerCapita = population && population.population > 0 ? vmt.total_vmt / population.population : 0;
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'Selected Location'
        };
      });

      // Calculate California average using the same approach
      // Get California state-level VMT using same methodology
      // Use LA County as a large representative sample and scale appropriately
      const californiaProxyVMT = await this.getVMTDataWithScaling(6037); // Los Angeles County
      
      const californiaVMTByYear = californiaProxyVMT
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      // Get California total population
      const { data: calPopData, error: calPopError } = await supabaseClient
        .from('population')
        .select('year, population')
        .order('year', { ascending: true });

      if (calPopError) {
        console.error('Error fetching California population data:', calPopError);
        throw calPopError;
      }

      // Aggregate California population by year
      const californiaPopByYear = calPopData
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_population += curr.population || 0;
          } else {
            acc.push({
              year: curr.year,
              total_population: curr.population || 0
            });
          }
          return acc;
        }, []);

      // Calculate California VMT per capita
      // Scale LA County VMT to represent California (LA County is ~25% of CA population)
      const californiaVMTPerCapita = californiaVMTByYear.map((vmt: any) => {
        const population = californiaPopByYear.find((p: any) => p.year === vmt.year);
        
        // Scale LA County VMT to approximate California state total
        // LA County represents about 25% of California's population and has higher VMT than state average
        const scalingFactor = 4.0; // Rough approximation to scale from LA County to state level
        const stateVMT = vmt.total_vmt * scalingFactor;
        
        const vmtPerCapita = population && population.total_population > 0 ? stateVMT / population.total_population : 0;
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'California Average'
        };
      });

      // Combine the data
      const combinedData = [...locationVMTPerCapita, ...californiaVMTPerCapita];

      return combinedData;

    } catch (error) {
      console.error('Error in getVMTPerCapitaData:', error);
      throw error;
    }
  }
}
